import { computed, readonly, ref, shallowRef, watch, type Ref } from 'vue';
import GeoJSON from 'ol/format/GeoJSON';
import { getArea } from 'ol/sphere';
import { useFilterStore, type VisiblePass } from './useFilterStore';
import { resolveSatelliteName, getSatMetadata, useAoiStore } from './useAoiStore';
import { isOroraTechConstellationSatellite } from './useConstellationGrouping';
import { computeIsDaytime } from './useSolarPosition';
import { clipTimesToAoi, getLatExtent } from './useAoiCoverageBuckets';
import { normalizePassDirection } from './passDirection';
import { normalizeUploadedGeoJson } from '@/uploadedGeometryValidation';

/**
 * Compute a simple centroid (average of all coordinate points) from a GeoJSON object.
 * Returns [longitude, latitude] or null if no coordinates found.
 */
function geojsonCentroid(geojson: Record<string, unknown>): [number, number] | null {
  let sumLon = 0;
  let sumLat = 0;
  let count = 0;

  const collectCoords = (coords: unknown): void => {
    if (!Array.isArray(coords) || coords.length === 0) return;
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      sumLon += coords[0] as number;
      sumLat += coords[1] as number;
      count++;
      return;
    }
    for (const child of coords) collectCoords(child);
  };

  const processGeometry = (geom: Record<string, unknown>): void => {
    if (geom.coordinates) collectCoords(geom.coordinates);
  };

  const doc = geojson;
  if (doc.type === 'FeatureCollection' && Array.isArray(doc.features)) {
    for (const feat of doc.features) {
      const geom = (feat as Record<string, unknown>)?.geometry as Record<string, unknown> | undefined;
      if (geom) processGeometry(geom);
    }
  } else if (doc.type === 'Feature') {
    const geom = doc.geometry as Record<string, unknown> | undefined;
    if (geom) processGeometry(geom);
  } else if (doc.coordinates) {
    processGeometry(doc);
  }

  if (count === 0) return null;
  return [sumLon / count, sumLat / count];
}

export interface MonitoredArea {
  id: string;
  name: string;
  shared: boolean;
  area_km2: number;
  modified_at: string;
  created_at: string;
  delivery_methods: ReadonlyArray<{ readonly method: string; readonly enabled: boolean }>;
  aoi_geojson: Record<string, unknown>;
  coverage_geojson: Record<string, unknown> | null;
  coverage_summary: string | null;
  queried_at: string | null;
  visible: boolean;
}

const STORAGE_KEY = 'overpass.monitoredAreas.v1';
const STORAGE_DB_NAME = 'overpassPlanner';
const STORAGE_DB_VERSION = 1;
const STORAGE_STORE_NAME = 'appState';
const areas = ref<MonitoredArea[]>([]);
const isHydrated = ref(typeof window === 'undefined');
const STORAGE_PERSIST_DELAY_MS = 150;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let persistChain: Promise<void> = Promise.resolve();

function cloneJsonValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function toStorageSnapshot(value: MonitoredArea[]): MonitoredArea[] {
  return value.map((area) => ({
    id: area.id,
    name: area.name,
    shared: area.shared,
    area_km2: area.area_km2,
    modified_at: area.modified_at,
    created_at: area.created_at,
    delivery_methods: area.delivery_methods.map((method) => ({ method: method.method, enabled: method.enabled })),
    aoi_geojson: cloneJsonValue(area.aoi_geojson),
    coverage_geojson: area.coverage_geojson ? cloneJsonValue(area.coverage_geojson) : null,
    coverage_summary: area.coverage_summary,
    queried_at: area.queried_at,
    visible: area.visible,
  }));
}

function parseIsoToEpochMs(value: string | null | undefined): number {
  const parsed = Date.parse(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function sortByLatestCalculated(areasToSort: MonitoredArea[]): MonitoredArea[] {
  return [...areasToSort].sort((a, b) => {
    const aQueried = parseIsoToEpochMs(a.queried_at);
    const bQueried = parseIsoToEpochMs(b.queried_at);
    if (aQueried === 0 && bQueried === 0) {
      return parseIsoToEpochMs(b.created_at) - parseIsoToEpochMs(a.created_at);
    }
    if (aQueried === 0) return 1;
    if (bQueried === 0) return -1;
    return bQueried - aQueried;
  });
}

function getAreasRecencyMs(value: MonitoredArea[]): number {
  let latest = 0;
  for (const area of value) {
    latest = Math.max(
      latest,
      parseIsoToEpochMs(area.created_at),
      parseIsoToEpochMs(area.modified_at),
      parseIsoToEpochMs(area.queried_at),
    );
  }
  return latest;
}

function persistAreasToLocalStorage(value: MonitoredArea[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore persistence failures (quota/private mode).
  }
}

function openStorageDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB unavailable'));
      return;
    }

    const request = window.indexedDB.open(STORAGE_DB_NAME, STORAGE_DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORAGE_STORE_NAME)) {
        db.createObjectStore(STORAGE_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB'));
  });
}

async function readAreasFromIndexedDb(): Promise<unknown | null> {
  if (typeof window === 'undefined' || !window.indexedDB) return null;

  let db: IDBDatabase | null = null;
  try {
    db = await openStorageDb();
    return await new Promise<unknown | null>((resolve, reject) => {
      const tx = db!.transaction(STORAGE_STORE_NAME, 'readonly');
      const store = tx.objectStore(STORAGE_STORE_NAME);
      const request = store.get(STORAGE_KEY);

      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error ?? new Error('Failed reading IndexedDB value'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    });
  } catch {
    return null;
  } finally {
    db?.close();
  }
}

async function writeAreasToIndexedDb(value: MonitoredArea[]): Promise<boolean> {
  if (typeof window === 'undefined' || !window.indexedDB) return false;

  let db: IDBDatabase | null = null;
  try {
    db = await openStorageDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db!.transaction(STORAGE_STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORAGE_STORE_NAME);
      store.put(value, STORAGE_KEY);
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    });
    return true;
  } catch {
    return false;
  } finally {
    db?.close();
  }
}

function persistAreasToStorage(value: MonitoredArea[]): void {
  const snapshot = toStorageSnapshot(value);
  persistChain = persistChain
    .then(async () => {
      await writeAreasToIndexedDb(snapshot);
    })
    .catch(() => {
      // Ignore persistence failures; app state should remain functional.
    });
}

function schedulePersistAreasToStorage(): void {
  if (typeof window === 'undefined') return;
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    persistAreasToStorage(areas.value);
  }, STORAGE_PERSIST_DELAY_MS);
}

function estimateAreaKm2(geojson: Record<string, unknown> | null): number {
  if (!geojson || typeof geojson !== 'object') return 0;

  try {
    const format = new GeoJSON();
    const features = format.readFeatures(geojson, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:4326' });
    let totalAreaSqMeters = 0;
    
    for (const feature of features) {
      const geometry = feature.getGeometry();
      if (geometry) {
        totalAreaSqMeters += getArea(geometry, { projection: 'EPSG:4326' });
      }
    }
    
    // Convert to sq kilometers and return, ensuring at least 0.01 rounded locally
    return Math.max(0.01, totalAreaSqMeters / 1e6);
  } catch (err) {
    console.error('Failed to calculate exact area:', err);
    return 0;
  }
}

function addAreaFromAoi(name: string, geojson: Record<string, unknown> | null): MonitoredArea {
  const now = new Date().toISOString();
  const safeName = (name || 'Uploaded AOI').trim() || 'Uploaded AOI';
  const normalized = geojson ? normalizeUploadedGeoJson(geojson) : null;
  const normalizedGeoJson = normalized?.geojson;
  const safeGeoJson = (normalizedGeoJson ?? geojson ?? { type: 'FeatureCollection', features: [] }) as Record<string, unknown>;

  const newArea: MonitoredArea = {
    id: `aoi-${Date.now()}-${Math.round(Math.random() * 1000)}`,
    name: safeName,
    shared: false,
    area_km2: estimateAreaKm2(safeGeoJson),
    created_at: now,
    modified_at: now,
    delivery_methods: [
      { method: 'browser', enabled: true },
      { method: 'activity', enabled: true },
    ],
    aoi_geojson: safeGeoJson,
    coverage_geojson: null,
    coverage_summary: null,
    queried_at: null,
    visible: false,
  };

  areas.value = [newArea, ...areas.value];
  return newArea;
}

function setAreaCoverage(
  areaId: string,
  coverageGeoJson: Record<string, unknown>,
  coverageSummary: string,
  queriedAt: string,
): void {
  const now = new Date().toISOString();
  areas.value = sortByLatestCalculated(areas.value.map((area) => {
    if (area.id !== areaId) return area;
    return {
      ...area,
      coverage_geojson: coverageGeoJson,
      coverage_summary: coverageSummary,
      queried_at: queriedAt,
      modified_at: now,
      visible: true,
    };
  }));
}

function setAreaVisibility(areaId: string, visible: boolean): void {
  areas.value = areas.value.map((area) => area.id === areaId ? { ...area, visible } : area);
}

function setVisibleAreaIds(areaIds: string[]): void {
  const visibleSet = new Set(areaIds);
  areas.value = areas.value.map((area) => ({
    ...area,
    visible: visibleSet.has(area.id),
  }));
}

function getAreaById(areaId: string): MonitoredArea | undefined {
  return areas.value.find((area) => area.id === areaId);
}

function removeArea(id: string): void {
  areas.value = areas.value.filter((area) => area.id !== id);
}

function renameArea(id: string, newName: string): void {
  const now = new Date().toISOString();
  areas.value = areas.value.map((area) =>
    area.id === id ? { ...area, name: newName, modified_at: now } : area,
  );
}

const hasAreas = computed(() => areas.value.length > 0);
const visibleAreas = computed(() => areas.value.filter((area) => area.visible));

function normalizeHydratedAreas(parsed: unknown): MonitoredArea[] {
  if (!Array.isArray(parsed)) return [];

  return parsed
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      const area = item as Partial<MonitoredArea>;
      if (!area.id || !area.name || !area.aoi_geojson) return null;
      const normalized = normalizeUploadedGeoJson(area.aoi_geojson);
      const normalizedGeoJson = normalized.geojson;
      if (normalized.error || !normalizedGeoJson) {
        console.warn('[monitored-areas] Dropping invalid stored AOI geometry:', area.name, normalized.error);
        return null;
      }
      return {
        id: area.id,
        name: area.name,
        shared: Boolean(area.shared),
        area_km2: typeof area.area_km2 === 'number' ? area.area_km2 : 0,
        modified_at: typeof area.modified_at === 'string' ? area.modified_at : new Date().toISOString(),
        created_at: typeof area.created_at === 'string' ? area.created_at : new Date().toISOString(),
        delivery_methods: Array.isArray(area.delivery_methods) ? area.delivery_methods : [{ method: 'browser', enabled: true }],
        aoi_geojson: normalizedGeoJson,
        coverage_geojson: area.coverage_geojson ?? null,
        coverage_summary: area.coverage_summary ?? null,
        queried_at: area.queried_at ?? null,
        visible: Boolean(area.visible),
      } as MonitoredArea;
    })
    .filter((area): area is MonitoredArea => area !== null);
}

function readAreasFromLocalStorage(): MonitoredArea[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return normalizeHydratedAreas(JSON.parse(raw));
  } catch {
    return [];
  }
}

async function hydrateFromStorage(): Promise<void> {
  try {
    const indexedDbValue = await readAreasFromIndexedDb();
    const indexedDbAreas = normalizeHydratedAreas(indexedDbValue);
    const localStorageAreas = readAreasFromLocalStorage();
    const indexedDbRecencyMs = getAreasRecencyMs(indexedDbAreas);
    const localStorageRecencyMs = getAreasRecencyMs(localStorageAreas);

    if (indexedDbAreas.length === 0 && localStorageAreas.length === 0) {
      areas.value = [];
      return;
    }

    if (localStorageAreas.length > 0 && localStorageRecencyMs > indexedDbRecencyMs) {
      areas.value = sortByLatestCalculated(localStorageAreas);
      persistAreasToStorage(localStorageAreas);
      return;
    }

    if (indexedDbAreas.length > 0) {
      areas.value = sortByLatestCalculated(indexedDbAreas);
      persistAreasToLocalStorage(indexedDbAreas);
      return;
    }

    areas.value = sortByLatestCalculated(localStorageAreas);
    if (localStorageAreas.length > 0) persistAreasToStorage(localStorageAreas);
  } finally {
    isHydrated.value = true;
  }
}

if (typeof window !== 'undefined') {
  void hydrateFromStorage();
}

watch(areas, () => {
  if (!isHydrated.value) return;
  persistAreasToLocalStorage(toStorageSnapshot(areas.value));
  schedulePersistAreasToStorage();
});

/**
 * Extract overpass-like entries from coverage GeoJSON of visible areas,
 * filtered by the current satellite selection (not by time range).
 *
 * Uses an async watcher that yields between areas via requestAnimationFrame
 * so the UI thread stays responsive during heavy pass list recalculations.
 */
const aoiPassesAll: Ref<VisiblePass[]> = shallowRef([]);
let _aoiPassGeneration = 0;

function _processAreaPasses(
  area: MonitoredArea,
  selectedUpper: Set<string>,
  expectedSource: string,
  seen: Set<string>,
  clippedTimes: Map<string, [number, number]>,
  result: VisiblePass[],
  standardForecastEndMs: number,
  passWithinForecastHorizon: (satellite: string, startMs: number, dataSource?: string) => boolean,
): void {
  if (!area.coverage_geojson) return;
  const doc = area.coverage_geojson as Record<string, unknown>;
  const features = (doc.type === 'FeatureCollection' && Array.isArray(doc.features))
    ? doc.features as Record<string, unknown>[]
    : [doc];

  const centroid = geojsonCentroid(area.aoi_geojson);
  const aoiLatExt = getLatExtent(area.aoi_geojson);

  for (const feat of features) {
    const props = ((feat as Record<string, unknown>).properties ?? feat) as Record<string, unknown>;
    const satName = resolveSatelliteName(props);
    if (!satName) continue;

    if (!selectedUpper.has(satName.toUpperCase())) continue;

    const dataSource = String(props.data_source ?? '');
    if (isOroraTechConstellationSatellite(satName)) {
      if (dataSource && dataSource !== expectedSource) continue;
    }

    const startStr = String(props.start_time ?? props.startTime ?? props.start ?? '');
    const endStr = String(props.end_time ?? props.endTime ?? props.end ?? '');
    if (!startStr || !endStr) continue;

    const startMs = new Date(startStr).getTime();
    const endMs = new Date(endStr).getTime();
    if (!passWithinForecastHorizon(satName, startMs, dataSource)) continue;

    const key = `${satName}-${startStr}-${endStr}`;

    const geom = (feat as Record<string, unknown>).geometry as Record<string, unknown> | undefined;
    // Already trimmed to actual AOI contact by the prediction service — re-clipping
    // would substitute a latitude-extent estimate for an exact answer.
    const [clippedStart, clippedEnd] = props.times_exact
      ? [startMs, endMs]
      : clipTimesToAoi(startMs, endMs, geom, area.aoi_geojson, aoiLatExt);

    const existing = clippedTimes.get(key);
    if (existing) {
      existing[0] = Math.min(existing[0], clippedStart);
      existing[1] = Math.max(existing[1], clippedEnd);
    } else {
      clippedTimes.set(key, [clippedStart, clippedEnd]);
    }

    if (seen.has(key)) continue;
    seen.add(key);

    const meta = getSatMetadata(satName);
    const sensorType = (props.sensor_type ?? props.sensor ?? props.sensorType ?? props.instrument) as string | undefined;
    const rawRes = props.spatial_res_m ?? props.resolution ?? props.spatialRes ?? props.spatial_resolution;
    const constell = (props.constellation ?? props.program) as string | undefined;
    const rawDaytime = props.is_daytime ?? props.isDaytime ?? props.daytime ?? props.daylight;

    const resolvedRes = (() => {
      if (typeof rawRes === 'number') return rawRes;
      if (typeof rawRes === 'string') { const n = parseFloat(rawRes); return isNaN(n) ? undefined : n; }
      return undefined;
    })();

    const resolvedDaytime = (() => {
      if (typeof rawDaytime === 'boolean') return rawDaytime;
      if (typeof rawDaytime === 'string') return rawDaytime.toLowerCase() === 'true' || rawDaytime.toLowerCase() === 'day';
      // The predictor reports the sun elevation it computed for this pass over this
      // area (requested via the sun_elevation_deg metadata selector). Prefer it to
      // re-deriving the same quantity from a footprint centroid, which answers a
      // slightly different question — where the swath is, not where the area is.
      const sunElevationDeg = Number(props.sun_elevation_deg);
      if (Number.isFinite(sunElevationDeg)) return sunElevationDeg > 0;
      if (centroid) {
        return computeIsDaytime(startMs, endMs, centroid[1], centroid[0]);
      }
      return undefined;
    })();
    const resolvedCloudCover = (() => {
      if (startMs > standardForecastEndMs && dataSource !== 'api') return null;
      const rawCloud = props.cloud_cover_pct;
      if (rawCloud == null) return null;
      const numeric = Number(rawCloud);
      if (!Number.isFinite(numeric)) return null;
      return Math.max(0, Math.min(100, Math.round(numeric)));
    })();

    result.push({
      id: `aoi-${key}`,
      name: satName,
      start_time: startStr,
      end_time: endStr,
      direction: normalizePassDirection(props.direction),
      sensor_type: sensorType ?? meta?.sensor_type,
      spatial_res_m: resolvedRes ?? meta?.spatial_res_m,
      constellation: constell ?? meta?.constellation,
      is_daytime: resolvedDaytime,
      cloud_cover_pct: resolvedCloudCover,
      covered_fraction: typeof props.covered_fraction === 'number' ? props.covered_fraction : null,
    });
  }
}

function _yieldToUI(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

{
  // Scoped watcher block — sets up async pass computation
  const {
    selectedSatelliteNames,
    otDataSourceFilter,
    standardForecastEndMs,
    passWithinForecastHorizon,
  } = useFilterStore();
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  watch(
    [visibleAreas, selectedSatelliteNames, otDataSourceFilter, standardForecastEndMs],
    () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        void _rebuildAoiPasses();
      }, 30);
    },
    { immediate: true },
  );

  async function _rebuildAoiPasses() {
    const generation = ++_aoiPassGeneration;
    const selectedUpper = new Set(selectedSatelliteNames.value.map((n) => n.toUpperCase()));

    if (selectedUpper.size === 0) {
      aoiPassesAll.value = [];
      return;
    }

    const expectedSource = otDataSourceFilter.value === 'predicted' ? 'api' : 'forest_client';

    const seen = new Set<string>();
    const result: VisiblePass[] = [];
    const clippedTimes = new Map<string, [number, number]>();
    const currentAreas = visibleAreas.value;

    for (let i = 0; i < currentAreas.length; i++) {
      _processAreaPasses(
        currentAreas[i],
        selectedUpper,
        expectedSource,
        seen,
        clippedTimes,
        result,
        standardForecastEndMs.value,
        passWithinForecastHorizon,
      );

      // Yield to the browser between areas to keep UI responsive
      if (i < currentAreas.length - 1) {
        await _yieldToUI();
        // Bail if a newer rebuild was requested while we yielded
        if (_aoiPassGeneration !== generation) return;
      }
    }

    // Apply clipped times
    for (const pass of result) {
      const key = pass.id.slice(4);
      const ct = clippedTimes.get(key);
      if (ct) {
        pass.start_time = new Date(ct[0]).toISOString();
        pass.end_time = new Date(ct[1]).toISOString();
      }
    }

    result.sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time));

    // Only apply if this is still the latest generation
    if (_aoiPassGeneration === generation) {
      aoiPassesAll.value = result;
    }
  }
}

/**
 * AOI pass list filtered by current time window.
 */
const aoiPasses = computed<VisiblePass[]>(() => {
  const { timeRange } = useFilterStore();
  const [rangeStart, rangeEnd] = timeRange.value;
  return aoiPassesAll.value.filter((pass) => {
    const startMs = new Date(pass.start_time).getTime();
    return startMs >= rangeStart && startMs <= rangeEnd;
  });
});

function setMultipleAreaCoverages(
  updates: Array<{
    areaId: string;
    coverageGeoJson: Record<string, unknown>;
    coverageSummary: string;
    queriedAt: string;
  }>,
): void {
  const now = new Date().toISOString();
  const updateMap = new Map(updates.map((u) => [u.areaId, u]));
  areas.value = sortByLatestCalculated(areas.value.map((area) => {
    const update = updateMap.get(area.id);
    if (!update) return area;
    return {
      ...area,
      coverage_geojson: update.coverageGeoJson,
      coverage_summary: update.coverageSummary,
      queried_at: update.queriedAt,
      modified_at: now,
      visible: true,
    };
  }));
}

const AOI_RANGE_REFRESH_DEBOUNCE_MS = 400;
let rangeRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let rangeRefreshController: AbortController | null = null;

async function refreshVisibleAreasForRange(range: readonly [number, number]): Promise<void> {
  rangeRefreshController?.abort();
  const controller = new AbortController();
  rangeRefreshController = controller;

  const currentAreas = areas.value.filter((area) => area.visible);
  if (currentAreas.length === 0) return;

  const { submitCoverage } = useAoiStore();
  const updates: Array<{
    areaId: string;
    coverageGeoJson: Record<string, unknown>;
    coverageSummary: string;
    queriedAt: string;
  }> = [];

  for (const area of currentAreas) {
    if (controller.signal.aborted) return;
    const result = await submitCoverage({
      geojson: area.aoi_geojson,
      quiet: true,
      range,
      signal: controller.signal,
    });
    if (controller.signal.aborted) return;
    if (result) {
      updates.push({
        areaId: area.id,
        coverageGeoJson: result.geojson,
        coverageSummary: result.summary,
        queriedAt: result.queriedAt,
      });
    }
  }

  if (updates.length > 0 && !controller.signal.aborted) {
    setMultipleAreaCoverages(updates);
  }
  if (rangeRefreshController === controller) {
    rangeRefreshController = null;
  }
}

{
  const { timeRange } = useFilterStore();
  watch(
    [() => timeRange.value[0], () => timeRange.value[1], isHydrated],
    ([startMs, endMs, hydrated]) => {
      if (rangeRefreshTimer) clearTimeout(rangeRefreshTimer);
      rangeRefreshController?.abort();
      if (!hydrated || endMs <= startMs) return;

      rangeRefreshTimer = setTimeout(() => {
        rangeRefreshTimer = null;
        void refreshVisibleAreasForRange([startMs, endMs]);
      }, AOI_RANGE_REFRESH_DEBOUNCE_MS);
    },
    { immediate: true },
  );
}

export function useMonitoredAreasStore() {
  return {
    areas: readonly(areas),
    isHydrated: readonly(isHydrated),
    hasAreas: readonly(hasAreas),
    visibleAreas: readonly(visibleAreas),
    aoiPassesAll,
    aoiPasses,
    addAreaFromAoi,
    setAreaCoverage,
    setMultipleAreaCoverages,
    setAreaVisibility,
    setVisibleAreaIds,
    getAreaById,
    removeArea,
    renameArea,
  };
}
